import { SidebarVariant } from "@/components/layout/components/sidebar/constants";
import { clientEnvModule } from "@/modules/client-env";
import { isMac } from "@/domains/platform/isMac";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { BackForwardButtons } from "../back-forward-buttons";

interface SidebarDesktopSectionProps {
  currentSidebarVariant: SidebarVariant;
  toggleSidebar: () => void;
  navigateBack: () => void;
  navigateForward: () => void;
  canNavigateBack: boolean;
  canNavigateForward: boolean;
}

export const SidebarDesktopSection = observer(function SidebarDesktopSection({
  currentSidebarVariant,
  navigateBack,
  navigateForward,
  canNavigateBack,
  canNavigateForward,
}: SidebarDesktopSectionProps) {
  const isDesktop = clientEnvModule.isDesktop();
  const isMacOS = isMac();

  const macOSTrafficLightsSpacerOrWindowsOverlaySpacer = isMacOS ? (
    <StoplightSpacer data-component="StoplightSpacer" />
  ) : (
    <OverlaySpacer data-component="OverlaySpacer" />
  );

  if (!isDesktop) return null;

  if (currentSidebarVariant === SidebarVariant.Collapsed) {
    return (
      <StyledWrapper $sidebarVariant={currentSidebarVariant} data-component="SidebarDesktopSection">
        {macOSTrafficLightsSpacerOrWindowsOverlaySpacer}
      </StyledWrapper>
    );
  }

  return (
    <StyledWrapper $sidebarVariant={currentSidebarVariant} data-component="SidebarDesktopSection">
      <LeftSection>{macOSTrafficLightsSpacerOrWindowsOverlaySpacer}</LeftSection>
      <RightSection>
        <BackForwardButtons
          navigateBack={navigateBack}
          navigateForward={navigateForward}
          canNavigateBack={canNavigateBack}
          canNavigateForward={canNavigateForward}
        />
      </RightSection>
    </StyledWrapper>
  );
});

const StyledWrapper = styled.div<{ $sidebarVariant: SidebarVariant }>`
  display: flex;
  flex-direction: row;
  justify-content: ${({ $sidebarVariant }) =>
    $sidebarVariant === SidebarVariant.Collapsed ? "center" : "space-between"};
  align-items: center;
  padding-inline: ${({ theme }) => theme.spacing["4"]};
  padding-top: ${({ theme }) => theme.spacing["2"]};
  -webkit-app-region: drag;
`;

const LeftSection = styled.div({
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-start",
  alignItems: "center",
  width: "100%",
});

const RightSection = styled.div({
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-end",
  alignItems: "center",
  width: "100%",
});

export const StoplightSpacer = styled.div({
  height: 20,
  width: 80,
  minWidth: 80,
});

// Space reserved for the global desktop menu overlay (Windows/Linux).
// Matches left offset (8px) + button size (~32px) + right gap (16px) = 56px.
const OverlaySpacer = styled.div({
  height: 20,
  width: 56,
});
